import { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CheckCircle2, AlertTriangle, ArrowRight, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import StepShell from "../StepShell";
import { usePracticeAreas } from "../useBriefing";
import { GOAL_LABELS, PROCESS_LABELS, TONE_LABELS, TOOL_LABELS } from "../questions";

/** Small labelled row used in the review summary. */
function Row({ label, value }) {
  return (
    <motion.div
      variants={{
        initial: { opacity: 0, y: 6 },
        animate: { opacity: 1, y: 0 },
      }}
      className="flex items-start justify-between gap-4 border-b border-border/60 py-3 last:border-0"
    >
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-right text-sm font-medium text-foreground">
        {value || <span className="text-muted-foreground">—</span>}
      </span>
    </motion.div>
  );
}

const PROVIDER_LABEL = {
  advbox: "AdvBox",
  projuris: "Projuris",
  none: "Não usa",
};

/**
 * Última etapa — resumo do briefing antes de concluir. A ação "Concluir" fica
 * no rodapé do wizard; aqui mostramos o que foi capturado e eventuais pendências.
 */
export default function BriefingReview({ briefing, onJumpToStep }) {
  const reduceMotion = useReducedMotion();
  const { data } = usePracticeAreas();
  const areas = data?.areas || [];
  const labelOf = (id) => areas.find((a) => a.id === id)?.label || id;

  const selected = briefing.areas?.selected || [];
  const goal = briefing.automationGoal || {};
  const tools = briefing.toolsInUse || {};
  const process = briefing.salesProcess || {};

  const toolsSummary = ["crm", "agenda", "signature", "billing"]
    .map((k) => tools[k] && TOOL_LABELS[k]?.[tools[k]])
    .filter(Boolean)
    .join(" · ");

  const processSummary = [
    process.qualifier && `Qualifica: ${PROCESS_LABELS.qualifier[process.qualifier] || process.qualifier}`,
    process.contractApprover &&
      `Contrato: ${PROCESS_LABELS.contractApprover[process.contractApprover] || process.contractApprover}`,
    process.followUp && `Follow-up: ${PROCESS_LABELS.followUp[process.followUp] || process.followUp}`,
  ]
    .filter(Boolean)
    .join(" · ");

  // Pending items shown proactively (no need to click "Concluir" first), each
  // deep-linked to the question that fixes it. Mirrors the backend validation
  // in implementationBriefingService.getCompletionBlockers — keep both in sync.
  const blockers = useMemo(() => {
    const list = [];
    if (!briefing.juridicalProvider) {
      list.push({
        label: "Defina a conexão do sistema jurídico",
        step: "juridico",
      });
    }
    if (!briefing.responsibleName || !briefing.responsibleWhatsapp) {
      list.push({
        label: "Informe o responsável (nome + WhatsApp)",
        step: "responsavel",
      });
    }
    if ((briefing.areas?.selected || []).length === 0) {
      list.push({
        label: "Selecione ao menos uma área de atuação",
        step: "areas",
      });
    }
    if (!briefing.primaryArea) {
      list.push({ label: "Defina a área principal", step: "area_principal" });
    }
    if (!briefing.automationGoal?.primary) {
      list.push({
        label: "Defina o objetivo principal da automação",
        step: "objetivo",
      });
    }
    return list;
  }, [briefing]);

  return (
    <StepShell
      title="Revisão do briefing"
      description="Confira os dados antes de concluir. Ao concluir, geramos um rascunho do seu agente e fluxo para a nossa equipe revisar."
    >
      <motion.div
        initial="initial"
        animate="animate"
        variants={{
          animate: { transition: { staggerChildren: reduceMotion ? 0 : 0.05 } },
        }}
        className="rounded-2xl border border-border bg-surface-inset/40 px-5"
      >
        <Row
          label="Objetivo principal"
          value={goal.primary ? GOAL_LABELS[goal.primary] || goal.primary : null}
        />
        <Row
          label="Sistema jurídico"
          value={PROVIDER_LABEL[briefing.juridicalProvider] || null}
        />
        <Row label="Responsável" value={briefing.responsibleName} />
        <Row label="WhatsApp" value={briefing.responsibleWhatsapp} />
        <Row
          label="Áreas atendidas"
          value={selected.length ? selected.map(labelOf).join(", ") : null}
        />
        <Row
          label="Área principal"
          value={briefing.primaryArea ? labelOf(briefing.primaryArea) : null}
        />
        <Row label="Ferramentas" value={toolsSummary || null} />
        <Row label="Processo comercial" value={processSummary || null} />
        <Row
          label="Assistente"
          value={
            [briefing.assistantName, briefing.assistantTone && TONE_LABELS[briefing.assistantTone]]
              .filter(Boolean)
              .join(" · ") || null
          }
        />
      </motion.div>

      {blockers.length > 0 ? (
        <div className="mt-5 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div>
              <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                Faltam alguns itens para concluir:
              </p>
              <p className="mt-0.5 text-xs text-amber-700/80 dark:text-amber-300/80">
                Clique em um item para ir direto ao ponto que precisa ajustar.
              </p>
            </div>
          </div>
          <ul className="mt-3 space-y-2">
            {blockers.map((b) => (
              <li key={`${b.step}:${b.label}`}>
                <button
                  type="button"
                  onClick={() => onJumpToStep?.(b.step)}
                  className="group flex w-full items-center justify-between gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-left text-sm text-amber-800 transition-colors hover:bg-amber-500/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50 dark:text-amber-200"
                >
                  <span>{b.label}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                    Ajustar
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="mt-5 flex items-center gap-2 rounded-xl border border-[rgb(var(--cyan)/0.3)] bg-[rgb(var(--cyan)/0.08)] p-4 text-sm text-[rgb(var(--cyan))]">
          <CheckCircle2 className="h-5 w-5" />
          Tudo certo. Clique em “Concluir briefing” para gerar o rascunho.
        </div>
      )}

      {!briefing.goLiveAt && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
          <span className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-300">
            <CalendarClock className="h-4 w-4 shrink-0" />
            Você não confirmou a reunião de kickoff e treinamento.
          </span>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 border-amber-500/40 text-amber-700 hover:bg-amber-500/15 dark:text-amber-200"
            onClick={() => onJumpToStep?.("kickoff")}
          >
            Agendar
          </Button>
        </div>
      )}
    </StepShell>
  );
}
